import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, EASE, DUR, Reveal, Stagger, StaggerItem } from './motion';
import { FAQ, useLang, pick } from './content';

function FaqItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  const reduce = useReducedMotion();
  return (
    <div className="card-paper px-5 py-1">
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-4 text-left group"
      >
        <span className="flex-1 text-[15px] font-medium text-ink-900 group-hover:text-accent-700 transition-colors">{q}</span>
        <motion.span
          animate={reduce ? undefined : { rotate: open ? 180 : 0 }}
          transition={{ duration: DUR.subtle, ease: EASE }}
          className="shrink-0 text-ink-400"
        >
          <ChevronDown size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={reduce ? undefined : { height: 'auto', opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: DUR.subtle, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-4 -mt-1 text-[14px] text-ink-600 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FaqSection() {
  const lang = useLang();
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="border-t border-ink-100 bg-paper-50/40">
      <div className="mx-auto max-w-3xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-600 font-semibold">{pick(FAQ.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance">
              {pick(FAQ.h2, lang)}
            </h2>
            <p className="mt-3 text-[14.5px] text-ink-600 leading-relaxed max-w-2xl mx-auto">{pick(FAQ.subtitle, lang)}</p>
          </div>
        </Reveal>

        <Stagger stagger={0.04} className="space-y-2">
          {FAQ.items.map((item, i) => (
            <StaggerItem key={i}>
              <FaqItem
                q={pick(item.q, lang)}
                a={pick(item.a, lang)}
                open={open === i}
                onToggle={() => setOpen(open === i ? null : i)}
              />
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-8 text-center text-[13px] text-ink-500">
          {pick(FAQ.support, lang)}{' '}
          <a href={`mailto:${FAQ.email}`} className="text-accent-600 hover:underline font-medium">{FAQ.email}</a>
        </div>
      </div>
    </section>
  );
}
